import React, { useRef, useEffect } from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import DescriptionIcon from '@mui/icons-material/Description';
import { MarkdownContent } from './MarkdownContent';
import { ThinkingIndicator } from './ThinkingIndicator';
import { stripJsonBlocks } from './utils/stripJson';
import { stripCitationTokens } from '@/features/narrative-citations/parseCitations';
import type { Attachment, StreamStatus, ThreadItemData, TurnGroup } from './types';

// Tool records can be endpoints in older history groups. Never render them as
// conversation content, including in a collapsed history preview.
const isConversationMessage = (item: ThreadItemData | null): item is ThreadItemData =>
  item?.type === 'user' || item?.type === 'ai-response';

const hasRenderableMessage = (item: ThreadItemData | null): item is ThreadItemData => {
  if (!isConversationMessage(item)) return false;
  if (item.type === 'user') {
    return Boolean(item.content?.trim() || item.attachments?.length);
  }
  return Boolean(stripCitationTokens(stripJsonBlocks(item.content || '')).trim());
};

// --- Attachment List Component ---

const AttachmentList: React.FC<{ attachments: Attachment[] }> = ({ attachments }) => {
  if (!attachments.length) return null;
  return (
    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {attachments.map((a) => {
        const icon =
          a.kind === 'image' ? (
            <ImageIcon sx={{ fontSize: 16 }} />
          ) : a.kind === 'pdf' ? (
            <PictureAsPdfIcon sx={{ fontSize: 16 }} />
          ) : a.kind === 'document' ? (
            <DescriptionIcon sx={{ fontSize: 16 }} />
          ) : (
            <AudioFileIcon sx={{ fontSize: 16 }} />
          );

        if (a.kind === 'image' && a.previewUrl) {
          return (
            <Box
              key={a.s3Key || a.fileName}
              component="img"
              src={a.previewUrl}
              alt={a.fileName}
              sx={{
                width: 120,
                height: 120,
                borderRadius: 1,
                objectFit: 'cover',
                border: 1,
                borderColor: 'divider',
              }}
            />
          );
        }
        return (
          <Chip
            key={a.s3Key || a.fileName}
            icon={icon}
            label={a.fileName}
            size="small"
            sx={{
              maxWidth: 240,
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          />
        );
      })}
    </Box>
  );
};

// --- Thread Item Component ---

interface ThreadItemProps {
  item: ThreadItemData;
}

const ThreadItem: React.FC<ThreadItemProps> = ({ item }) => {
  if (!hasRenderableMessage(item)) {
    return null;
  }

  // User message
  if (item.type === 'user') {
    return (
      <Box sx={{ mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 3,
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            bgcolor: 'background.paper',
          }}
        >
          {item.content?.trim() && <MarkdownContent content={item.content} />}
          {item.attachments && item.attachments.length > 0 && (
            <AttachmentList attachments={item.attachments} />
          )}
        </Paper>
      </Box>
    );
  }

  // AI Response
  if (item.type === 'ai-response') {
    const cleanContent = stripJsonBlocks(item.content || '');
    // If the model emitted nothing but JSON, hide the bubble entirely.
    if (!cleanContent) return null;

    return (
      <Box sx={{ mb: 3 }}>
        <MarkdownContent content={cleanContent} />
      </Box>
    );
  }

  return null;
};

// --- Historical Turn Collapsed Component ---

interface HistoricalTurnCollapsedProps {
  group: TurnGroup;
  onExpand: () => void;
}

const HistoricalTurnCollapsed: React.FC<HistoricalTurnCollapsedProps> = ({ group, onExpand }) => {
  const previewMessage = [group.firstMessage, group.lastMessage]
    .find((item) => hasRenderableMessage(item) && item.content?.trim());
  const rawPreview = previewMessage?.content?.trim() || '';
  const previewText = previewMessage?.type === 'user'
    ? rawPreview
    : stripCitationTokens(stripJsonBlocks(rawPreview));
  const truncatedPreview = previewText.length > 80
    ? previewText.substring(0, 80) + '...'
    : previewText;

  return (
    <Box
      onClick={onExpand}
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: 1,
        bgcolor: 'action.hover',
        border: 1,
        borderColor: 'divider',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        '&:hover': {
          bgcolor: 'action.selected',
        },
        transition: 'background-color 0.2s',
      }}
    >
      <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
      <Chip
        icon={<HistoryIcon sx={{ fontSize: 14 }} />}
        label="History"
        size="small"
        sx={{
          height: 20,
          fontSize: 11,
          bgcolor: 'action.selected',
          '& .MuiChip-icon': { color: 'text.secondary' },
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {previewMessage?.type === 'user' ? 'User: ' : ''}
          {truncatedPreview}
        </Typography>
      </Box>
    </Box>
  );
};

// --- Turn Group Component ---

interface TurnGroupItemProps {
  group: TurnGroup;
}

const TurnGroupItem: React.FC<TurnGroupItemProps> = ({ group }) => {
  const [isExpanded, setIsExpanded] = React.useState(!group.isHistorical);

  // If historical and collapsed, show collapsed summary
  if (!isExpanded && group.isHistorical) {
    return (
      <HistoricalTurnCollapsed
        group={group}
        onExpand={() => setIsExpanded(true)}
      />
    );
  }

  return (
    <Box sx={group.isHistorical ? {
      borderLeft: 2,
      borderColor: 'divider',
      pl: 2,
      ml: -2,
      position: 'relative',
    } : undefined}>
      {/* History indicator for expanded historical turns */}
      {group.isHistorical && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Chip
            icon={<HistoryIcon sx={{ fontSize: 14 }} />}
            label="History"
            size="small"
            onClick={() => setIsExpanded(false)}
            sx={{
              height: 20,
              fontSize: 11,
              bgcolor: 'action.selected',
              cursor: 'pointer',
              '& .MuiChip-icon': { color: 'text.secondary' },
              '& .MuiChip-label': { px: 0.75 },
              '&:hover': { bgcolor: 'action.focus' },
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={() => setIsExpanded(false)}
          >
            Click to collapse
          </Typography>
        </Box>
      )}

      {/* First user message - always visible and expanded */}
      {group.firstMessage && (
        <ThreadItem item={group.firstMessage} />
      )}

      {/* Final message - always visible and expanded */}
      {group.lastMessage && (
        <ThreadItem item={group.lastMessage} />
      )}
    </Box>
  );
};

// --- Main Component ---

interface ChatMessageListProps {
  turnGroups: TurnGroup[];
  status: StreamStatus;
  isLoadingHistory?: boolean;
  /** Hint shown on an empty thread; defaults to the report-chat copy. */
  emptyStateText?: string;
}

export const ChatMessageList: React.FC<ChatMessageListProps> = ({
  turnGroups,
  status,
  isLoadingHistory = false,
  emptyStateText = 'Start a conversation with the agent',
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const visibleGroups = turnGroups.filter((group) =>
    [group.firstMessage, group.lastMessage].some(hasRenderableMessage)
  );

  // Auto-scroll
  useEffect(() => {
    if (isLoadingHistory || !messagesEndRef.current) return;

    const behavior = status === 'streaming' ? 'smooth' : 'auto';
    messagesEndRef.current.scrollIntoView({ behavior, block: 'end' });
  }, [visibleGroups.length, status, isLoadingHistory]);

  return (
    <Box
      ref={listRef}
      sx={{
        flex: 1,
        overflowY: 'auto',
        px: 3,
        py: 4,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      <Box sx={{ maxWidth: '896px', mx: 'auto', width: '100%' }}>
        {isLoadingHistory && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography sx={{ color: 'text.secondary' }}>Loading...</Typography>
          </Box>
        )}

        {visibleGroups.map((group) => (
          <TurnGroupItem
            key={`turn-${group.turn}`}
            group={group}
          />
        ))}

        {status === 'streaming' && !isLoadingHistory && <ThinkingIndicator />}

        {/* Invisible element to scroll to */}
        <div ref={messagesEndRef} />

        {visibleGroups.length === 0 && !isLoadingHistory && status !== 'streaming' && (
          <Box sx={{ textAlign: 'center', mt: 10, opacity: 0.5 }}>
            <Typography>{emptyStateText}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ChatMessageList;
